import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Modal,
  SpaceBetween,
  Spinner,
  Tabs,
} from "@amzn/awsui-components-react";
import { OmsProduct, OmsReport } from "../../lusca-search-api/generated-src";
import useOmsReport from "../../hooks/useOmsReport";
import useOmsExport from "../../hooks/useOmsExport";
import useOmsFilterCriteria from "../../hooks/useOmsFilterCriteria";
import OMSExportPanel, { OMSExportActions } from "./OMSExportPanel";
import OMSRecordFilters, { AppliedOmsFilters } from "./OMSRecordFilters";
import OMSReportFreshness from "./OMSReportFreshness";
import { fileSize } from "./omsReportFormatting";
import "./omsReportDownload.scss";

function refreshedAt(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

function ReportSummary({
  report,
  source = false,
}: {
  report: OmsReport;
  source?: boolean;
}) {
  return (
    <dl className="oms-report-download__summary">
      <div>
        <dt>Data refreshed</dt>
        <dd>
          <time dateTime={report.sourceRefreshedAt}>
            {refreshedAt(report.sourceRefreshedAt)}
          </time>
        </dd>
      </div>
      <div>
        <dt>{source ? "Source rows" : "Report rows"}</dt>
        <dd>{report.rowCount.toLocaleString()}</dd>
      </div>
      <div>
        <dt>{source ? "Source size" : "Report size"}</dt>
        <dd>{fileSize(report.totalBytes)}</dd>
      </div>
    </dl>
  );
}

function ExportDialog({
  product,
  productLabel,
  report,
  onClose,
}: {
  product: OmsProduct;
  productLabel: string;
  report: OmsReport;
  onClose: () => void;
}) {
  const [tab, setTab] = useState("full");
  const full = useOmsExport(product, report.snapshotId, "full");
  const filtered = useOmsExport(product, report.snapshotId, "filtered");
  const criteria = useOmsFilterCriteria(report, filtered.job.data);
  const current = tab === "full" ? full : filtered;
  const filteredActive = Boolean(filtered.jobId) || filtered.create.isLoading;
  const acceptedFilters = filtered.job.data;
  const fullCsvFiles = full.job.data?.csvFiles || 1;
  const filteredCsvFiles = filtered.job.data?.csvFiles || 1;
  return (
    <Modal
      visible
      onDismiss={onClose}
      header={`Export ${productLabel} report`}
      size="large"
      position="top"
      closeAriaLabel="Close report export"
      footer={
        <div className="oms-report-download__footer">
          <Button onClick={onClose}>Close</Button>
          <OMSExportActions
            state={current}
            mode={tab === "full" ? "full" : "filtered"}
            filters={tab === "full" ? [] : criteria.filters}
            operation={tab === "full" ? "and" : criteria.operation}
            valid={tab === "full" || criteria.valid}
          />
        </div>
      }
    >
      <SpaceBetween size="m">
        <ReportSummary report={report} source />
        <OMSReportFreshness sourceRefreshedAt={report.sourceRefreshedAt} />
        {report.rowCount === 0 && !current.jobId && (
          <Alert type="info" header="Empty report">
            The export will contain headers only.
          </Alert>
        )}
        <Tabs
          activeTabId={tab}
          onChange={({ detail }) => setTab(detail.activeTabId)}
          tabs={[
            {
              id: "full",
              label: "Full report",
              content: (
                <SpaceBetween size="m">
                  {!full.jobId && (
                    <Box>
                      Download all rows from the scheduled report. Dashboard
                      filters do not apply.
                    </Box>
                  )}
                  {fullCsvFiles > 1 && (
                    <Box color="text-body-secondary">
                      Prepared ZIP contains {fullCsvFiles.toLocaleString()} CSV
                      files. Create a new export for one CSV.
                    </Box>
                  )}
                  <OMSExportPanel state={full} mode="full" />
                </SpaceBetween>
              ),
            },
            {
              id: "filtered",
              label: "Filtered export",
              content: (
                <SpaceBetween size="m">
                  {!filteredActive && (
                    <OMSRecordFilters report={report} state={criteria} />
                  )}
                  {filteredActive &&
                    (acceptedFilters || filtered.create.isLoading) && (
                      <AppliedOmsFilters
                        report={report}
                        filters={acceptedFilters?.filters || criteria.filters}
                        operation={
                          acceptedFilters?.operation || criteria.operation
                        }
                      />
                    )}
                  <OMSExportPanel state={filtered} mode="filtered" />
                  {filteredCsvFiles > 1 && (
                    <Box color="text-body-secondary">
                      Prepared ZIP contains {filteredCsvFiles.toLocaleString()}{" "}
                      CSV files. Create a new export for one CSV.
                    </Box>
                  )}
                </SpaceBetween>
              ),
            },
          ]}
        />
        {(!current.jobId || current.job.data?.csvFiles === 1) && (
          <Box color="text-body-secondary">
            The ZIP includes one CSV with column headers and a report summary.
          </Box>
        )}
      </SpaceBetween>
    </Modal>
  );
}

function unavailableMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 404) return "No completed scheduled report is available yet.";
  if (status === 403) return "You do not have access to download this report.";
  return "The report is temporarily unavailable. Refresh to try again.";
}

export default function OMSReportDownload({
  product,
}: {
  product: OmsProduct;
}) {
  const query = useOmsReport(product);
  const [snapshot, setSnapshot] = useState<OmsReport | null>(null);
  const productLabel = {
    [OmsProduct.Channels]: "PV Channels",
    [OmsProduct.Addon]: "PV Addon",
    [OmsProduct.Tvod]: "PV TVOD",
  }[product];

  return (
    <>
      <Container
        header={
          <Header
            variant="h2"
            description="Export the complete scheduled report or choose which rows to include."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="refresh"
                  ariaLabel="Refresh scheduled report"
                  loading={query.isFetching}
                  loadingText="Refreshing scheduled report"
                  wrapText={false}
                  onClick={() => {
                    query.refetch().catch(() => undefined);
                  }}
                />
                <Button
                  variant="primary"
                  iconName="download"
                  wrapText={false}
                  ariaHaspopup="dialog"
                  disabled={!query.data || query.isFetching || query.isError}
                  onClick={() => {
                    setSnapshot(query.data || null);
                  }}
                >
                  Export report
                </Button>
              </SpaceBetween>
            }
          >
            Scheduled report
          </Header>
        }
      >
        <SpaceBetween size="xs">
          {query.isLoading && (
            <div className="oms-report-download__status" role="status">
              <Spinner />
              <span>Loading scheduled report…</span>
            </div>
          )}
          {query.isError && (
            <Alert
              type={
                (query.error as { response?: { status?: number } })?.response
                  ?.status === 404
                  ? "info"
                  : "warning"
              }
              header="Report unavailable"
            >
              {unavailableMessage(query.error)}
            </Alert>
          )}
          {query.data && !query.isError && (
            <>
              <ReportSummary report={query.data} />
              <OMSReportFreshness
                sourceRefreshedAt={query.data.sourceRefreshedAt}
              />
            </>
          )}
          {query.isFetching && !query.isLoading && (
            <div role="status">
              <Box color="text-body-secondary">Refreshing report details…</Box>
            </div>
          )}
        </SpaceBetween>
      </Container>
      {snapshot && (
        <ExportDialog
          product={product}
          productLabel={productLabel}
          report={snapshot}
          onClose={() => setSnapshot(null)}
        />
      )}
    </>
  );
}
